import React, { useContext, useEffect, useState } from "react";
import bloqueosContext from "../../context/bloqueos/bloqueosContext";
import Swal from "sweetalert2";
import "./BloqueosSubtabla.css";

const BloqueosSubtabla = ({ activoID, tablaSeleccionada }) => {
  const { bloqueosList, obtenerBloqueosActivos, eliminarBloqueosMultiples } = useContext(bloqueosContext);
  const [bloqueosDelActivo, setBloqueosDelActivo] = useState([]);
  const [bloqueosSeleccionados, setBloqueosSeleccionados] = useState([]);

  useEffect(() => {
    // Cargar bloqueos solo cuando se selecciona esta pestaña
    if (tablaSeleccionada === "bloqueos" && activoID) {
      obtenerBloqueosActivos();
    }
  }, [tablaSeleccionada, activoID]);

  useEffect(() => {
    // Filtrar bloqueos que corresponden a este activo
    if (bloqueosList && activoID) {
      const bloqueosFiltered = bloqueosList.filter(
        (b) => b.activoID === activoID || b.activoID?._id === activoID
      );
      // Ordenar por fecha de inicio descendente (más recientes primero)
      bloqueosFiltered.sort((a, b) => new Date(b.fechaInicio) - new Date(a.fechaInicio));
      setBloqueosDelActivo(bloqueosFiltered);
    }
  }, [bloqueosList, activoID]);

  const formatearFecha = (fecha) => {
    return new Date(fecha).toLocaleDateString("es-AR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getBadgeColor = (tipo) => {
    const colors = {
      MANTENIMIENTO: "#ffc107",
      EVENTO_PRIVADO: "#17a2b8",
      CLIMA: "#6c757d",
      CLASES: "#0d6efd",
      OTRO: "#343a40",
    };
    return colors[tipo] || "#343a40";
  };

  const getEstadoColor = (estado) => {
    const colors = {
      ACTIVO: "#28a745",
      COMPLETADO: "#007bff",
      CANCELADO: "#dc3545",
    };
    return colors[estado] || "#6c757d";
  };

  // Manejar selección individual
  const handleSeleccionIndividual = (bloqueoID) => {
    setBloqueosSeleccionados((prev) => {
      if (prev.includes(bloqueoID)) {
        return prev.filter((id) => id !== bloqueoID);
      } else {
        return [...prev, bloqueoID];
      }
    });
  };

  // Manejar selección de todos
  const handleSeleccionarTodos = () => {
    if (bloqueosSeleccionados.length === bloqueosDelActivo.length) {
      // Si todos están seleccionados, deseleccionar todos
      setBloqueosSeleccionados([]);
    } else {
      // Seleccionar todos
      setBloqueosSeleccionados(bloqueosDelActivo.map((b) => b._id));
    }
  };

  // Eliminar bloqueos seleccionados
  const handleEliminarSeleccionados = async () => {
    if (bloqueosSeleccionados.length === 0) return;

    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: `Se eliminarán ${bloqueosSeleccionados.length} bloqueo(s). Esta acción no se puede deshacer.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      customClass: {
        container: 'swal-high-z'
      }
    });

    if (result.isConfirmed) {
      try {
        await eliminarBloqueosMultiples(bloqueosSeleccionados);
        setBloqueosSeleccionados([]);
      } catch (error) {
        console.error("Error al eliminar bloqueos:", error);
      }
    }
  };

  return (
    <div
      className={`${
        tablaSeleccionada !== "bloqueos" && "inactive"
      } bloqueosSubtablaDiv`}
    >
      <div className="headerConAcciones">
        <label className="tituloSubtabla">Historial de Bloqueos</label>
        {bloqueosSeleccionados.length > 0 && (
          <button
            className="btnEliminarSeleccionados"
            onClick={handleEliminarSeleccionados}
          >
            Eliminar seleccionados ({bloqueosSeleccionados.length})
          </button>
        )}
      </div>

      {bloqueosDelActivo.length === 0 ? (
        <div className="noBloqueosMessage">
          <p>No hay bloqueos registrados para esta cancha</p>
        </div>
      ) : (
        <>
          <div className="subtablaHeader">
            <div className="labelCheckbox">
              <input
                type="checkbox"
                checked={bloqueosSeleccionados.length === bloqueosDelActivo.length && bloqueosDelActivo.length > 0}
                onChange={handleSeleccionarTodos}
                title="Seleccionar todos"
              />
            </div>
            <label className="labelFechaInicio">Desde</label>
            <label className="labelFechaFin">Hasta</label>
            <label className="labelHorario">Horario</label>
            <label className="labelTipo">Tipo</label>
            <label className="labelDescripcion">Descripción</label>
            <label className="labelEstado">Estado</label>
            <label className="labelUsuario">Usuario</label>
          </div>
          <div className="bodyTable">
            {bloqueosDelActivo.map((bloqueo, i) => (
              <div key={bloqueo._id || i} className="bloqueoRow">
                <div className="labelCheckbox">
                  <input
                    type="checkbox"
                    checked={bloqueosSeleccionados.includes(bloqueo._id)}
                    onChange={() => handleSeleccionIndividual(bloqueo._id)}
                  />
                </div>
                <div className="labelFechaInicio">
                  {formatearFecha(bloqueo.fechaInicio)}
                </div>
                <div className="labelFechaFin">
                  {formatearFecha(bloqueo.fechaFin)}
                </div>
                <div className="labelHorario">
                  {bloqueo.horaInicio && bloqueo.horaFin ? (
                    <>
                      {bloqueo.horaInicio} - {bloqueo.horaFin}
                    </>
                  ) : (
                    <span className="textMuted">Día completo</span>
                  )}
                </div>
                <div className="labelTipo">
                  <span
                    className="tipoBadge"
                    style={{ backgroundColor: getBadgeColor(bloqueo.tipo) }}
                  >
                    {bloqueo.tipo}
                  </span>
                </div>
                <div className="labelDescripcion" title={bloqueo.descripcion}>
                  {bloqueo.descripcion}
                </div>
                <div className="labelEstado">
                  <span
                    className="estadoBadge"
                    style={{
                      backgroundColor: getEstadoColor(bloqueo.estado),
                    }}
                  >
                    {bloqueo.estado}
                  </span>
                </div>
                <div className="labelUsuario">
                  {bloqueo.userNameAlt || "Sistema"}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default React.memo(BloqueosSubtabla);
